import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getBaseRoles, getPermissions, createBaseRole, updateBaseRole, deleteBaseRole,
          getRoles, createRole, updateRole, deleteRole } from '../../api/roleAPI';
import { log, getStackTrace } from "@src/Log.init";

export const retrieveBaseRoles = createAsyncThunk(
  'role/retrieveBaseRoles',
  async (arg, { getState }) => {
    const response = await getBaseRoles(getState().session.sessionToken);
    return response;
  })

export const retrieveRoles = createAsyncThunk(
  'role/retrieveRoles',
  async (arg, { getState }) => {
    const response = await getRoles(getState().session.sessionToken);
    return response;
  })

export const retrievePermissions = createAsyncThunk(
  'role/retrievePermissions',
  async (arg, { getState }) => {
      const response = await getPermissions(getState().session.sessionToken);
      return response;
  })

export const addBaseRole = createAsyncThunk(
    'role/addCurrentBaseRole',
    async (arg, { getState, dispatch }) => {
        const response = await createBaseRole(getState().session.sessionToken, arg);
        await dispatch(addToBaseRolesList(response));
    return response;
})

export const updateCurrentBaseRole = createAsyncThunk(
  'role/updateCurrentBaseRole',
  async (arg, { getState, dispatch }) => {
      const response = await updateBaseRole(getState().session.sessionToken, arg);
      await dispatch(updateBaseRolesList(response));
  return response;
})

export const deleteCurrentBaseRole = createAsyncThunk(
  'role/deleteCurrentBaseRole',
  async (arg, { getState, dispatch }) => {
      const response = await deleteBaseRole(getState().session.sessionToken, arg);
      await dispatch(removeBaseRoleFromList(arg));
  return response;
})

export const addRole = createAsyncThunk(
  'role/addCurrentRole',
  async (arg, { getState, dispatch }) => {
      const response = await createRole(getState().session.sessionToken, arg);
      await dispatch(addToRolesList(response));
  return response;
})

export const updateCurrentRole = createAsyncThunk(
'role/updateCurrentRole',
async (arg, { getState, dispatch }) => {
    const response = await updateRole(getState().session.sessionToken, arg);
    await dispatch(updateRolesList(response));
return response;
})

export const deleteCurrentRole = createAsyncThunk(
'role/deleteCurrentRole',
async (arg, { getState, dispatch }) => {
    const response = await deleteRole(getState().session.sessionToken, arg);
    await dispatch(removeRoleFromList(arg));
return response;
})

const populatePermissions = (state, action) => {
    state.permissions = action.payload;
}

const populateBaseRoles = (state, action) => {
    state.baseRoles = action.payload;
}

const populateRoles = (state, action) => {
  state.roles = action.payload;
}

export const roleSlice = createSlice({
  name: 'role',
  initialState: {
    permissions: [],
    baseRoles: [],
    roles: []
  },
  reducers: {
    updatePermissionsList: (state, action) => {
        const newList = [...state.permissions];        
        state.permissions = newList.map( entry => entry._id === action.payload._id ? action.payload: entry);
      },  
    updateBaseRolesList: (state, action) => {
        const newList = [...state.baseRoles];        
        state.baseRoles = newList.map( entry => entry._id === action.payload._id ? action.payload: entry);
      },  
    addToBaseRolesList: (state, action) => {
        const newList = [...state.baseRoles];
        newList.push(action.payload);      
        state.baseRoles = newList;
      },  
    removeBaseRoleFromList: (state, action) => {
        const newList = state.baseRoles.filter(entry => entry._id !== action.payload._id);
        state.baseRoles = newList;
      },  
    updateRolesList: (state, action) => {
        const newList = [...state.roles];        
        state.roles = newList.map( entry => entry._id === action.payload._id ? action.payload: entry);
      },  
    addToRolesList: (state, action) => {
        const newList = [...state.roles];
        newList.push(action.payload);      
        state.roles = newList;
      },  
    removeRoleFromList: (state, action) => {
        const newList = state.roles.filter(entry => entry._id !== action.payload._id);
        state.roles = newList;
      },  
  },
  extraReducers: {
    [retrievePermissions.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "retrieving permissions..."});
    },
    [retrievePermissions.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "Retrieve permissions successfully"});
      populatePermissions(state, action);
    },
    [retrievePermissions.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "permissions retrieval failed...", meta_data: getStackTrace()});
    },
    [retrieveBaseRoles.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "retrieving base roles..."});
    },
    [retrieveBaseRoles.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "Retrieve base roles successfully"});
      populateBaseRoles(state, action);
    },
    [retrieveBaseRoles.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "base roles retrieval failed...", meta_data: getStackTrace()});
    },
    [addBaseRole.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "adding base role..."});
    },
    [addBaseRole.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "base role add successful!"});
    },
    [addBaseRole.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "base role add failed...", meta_data: getStackTrace()});
    },
    [updateCurrentBaseRole.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "updating base role..."});
    },
    [updateCurrentBaseRole.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "base role update successful!"});
    },
    [updateCurrentBaseRole.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "base role update failed...", meta_data: getStackTrace()});
    },
    [deleteCurrentBaseRole.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "deleting base role..."});
    },
    [deleteCurrentBaseRole.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "base role delete successful!"});
    },
    [deleteCurrentBaseRole.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "base role delete failed...", meta_data: getStackTrace()});
    },
    [retrieveRoles.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "retrieving roles..."});
    },
    [retrieveRoles.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "Retrieve roles successfully"});
      populateRoles(state, action);
    },
    [retrieveRoles.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "roles retrieval failed...", meta_data: getStackTrace()});
    },
    [addRole.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "adding role..."});
    },
    [addRole.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "role add successful!"});
    },
    [addRole.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "role add failed...", meta_data: getStackTrace()});
    },
    [updateCurrentRole.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "updating role..."});
    },
    [updateCurrentRole.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "role update successful!"});
    },
    [updateCurrentRole.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "role update failed...", meta_data: getStackTrace()});
    },
    [deleteCurrentRole.pending]: (state, action) => {
      log.debug({message_type: "Role", message: "deleting role..."});
    },
    [deleteCurrentRole.fulfilled]: (state, action) => {
      log.info({message_type: "Role", message: "role delete successful!"});
    },
    [deleteCurrentRole.rejected]: (state, action) => {
      log.error({message_type: "SystemError", message: "role delete failed...", meta_data: getStackTrace()});
    },
  },
});

export const { updatePermissionsList,
               updateBaseRolesList,
               addToBaseRolesList,
               removeBaseRoleFromList,
               updateRolesList,
               addToRolesList,
               removeRoleFromList
  } = roleSlice.actions;

export const selectBasePermissions = state => state.role.permissions.filter(role => { return (role.application === 'Console' || role.application === 'NT')});
export const selectPermissions = state => state.role.permissions.filter(role => role.application === 'MIST');
export const selectBaseRoles = state => state.role.baseRoles;
export const selectRoles = state => state.role.roles;

export default roleSlice.reducer;
